/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import { Image } from 'site-modules/shared/components/image/image';
import { SOURCES } from 'client/utils/image-helpers';
import { YearDropdown } from 'site-modules/shared/components/ymms-dropdowns/year-dropdown/year-dropdown';
import { PUB_STATES } from 'client/constants/pub-states';
import { MakeDropdown } from 'site-modules/shared/components/ymms-dropdowns/make-dropdown/make-dropdown';
import { ModelDropdown } from 'site-modules/shared/components/ymms-dropdowns/model-dropdown/model-dropdown';
import { Link } from 'site-modules/shared/components/link/link';
import { buildAppraisalHistoryUrl } from 'site-modules/shared/utils/appraisal/appraisal-url-builders';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';
import './track-car-value-module.scss';

const CREATIVE_ID = 'edm-entry-track-car-value-module';
const CTA_TEXT = 'See pricing history';

export class TrackCarValueModuleUI extends Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    btnColor: PropTypes.string,
    className: PropTypes.string,
  };

  static defaultProps = {
    isMobile: false,
    btnColor: 'blue-50',
    className: '',
  };

  state = {
    make: null,
    model: null,
    year: null,
  };

  setYear = ({ year }) =>
    this.setState({
      year,
      make: null,
      model: null,
    });

  setMake = ({ niceName }) => this.setState({ make: niceName, model: null });

  setModel = ({ niceId }) => this.setState({ model: niceId });

  fireTracking = ({ eventType, actionName, actionCause, subactionName, value }) => {
    EventToolbox.fireTrackAction({
      event_type: eventType,
      event_data: {
        action_name: actionName,
        subaction_name: subactionName,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: actionCause,
        creative_id: CREATIVE_ID,
        value,
      },
    });
  };

  trackSubmit = () => {
    const { year, make, model } = this.state;
    const selectAction = {
      eventType: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      actionName: TrackingConstant.ACTION_MMY_SELECT,
      actionCause: TrackingConstant.SELECT_CHANGE,
    };

    this.fireTracking({ ...selectAction, subactionName: TrackingConstant.YEAR_SELECT, value: year });
    this.fireTracking({ ...selectAction, subactionName: TrackingConstant.MAKE_SELECT, value: make });
    this.fireTracking({ ...selectAction, subactionName: TrackingConstant.MODEL_SELECT, value: model });
  };

  renderSubheading = () => (
    <p className="size-16 text-gray-darker mb-0 mb-lg-1_75 pe-1 pe-md-0">
      Used car values are constantly changing. Edmunds lets you track your vehicle&apos;s value over time so you can
      decide when to sell or trade in.
    </p>
  );

  render() {
    const { isMobile, btnColor, className } = this.props;
    const { year, make, model } = this.state;
    const isBtnDisabled = !(make && model && year);
    const appraisalHistoryUrl = buildAppraisalHistoryUrl({
      makeSlug: make,
      modelSlug: model,
      year,
    });
    return (
      <fieldset
        className={classnames('track-car-value-module', className)}
        data-tracking-parent={CREATIVE_ID}
        data-tracking-value="track-car-value"
      >
        <legend className="d-flex align-items-end mb-1_75 mb-lg-0">
          <div className="pe-md-1_75">
            <p className="text-gray-darkest text-uppercase size-12 fw-bold mb-0_5">See Edmunds pricing data</p>
            <p className="heading-3 mb-1">
              Has Your Car&apos;s <span className="text-primary-darker">Value Changed?</span>
            </p>
            {isMobile && (
              <div className="d-flex align-items-top mb-1_75 mb-lg-0">
                {this.renderSubheading()}
                <Image
                  imageId="appraisal/tcv-page-cta/mobile-graph.svg"
                  alt="Price history graph example"
                  source={SOURCES.STATIC}
                  lazy
                  width={72}
                  height={72}
                  className="ps-4"
                />
              </div>
            )}
            {!isMobile && this.renderSubheading()}
          </div>
          {!isMobile && (
            <Image
              imageId="appraisal/tcv-page-cta/desktop-graph.svg"
              alt="Price history graph example"
              source={SOURCES.STATIC}
              lazy
              width={196}
              height={193}
            />
          )}
        </legend>
        <div className="mt-lg-1_5">
          <Row>
            <Col xs={12} md={2} className="mb-1 mb-md-0">
              <YearDropdown
                name="select-year"
                toggle="Year"
                valueKey="year"
                labelKey="year"
                labelText="Year"
                value={year}
                onChange={this.setYear}
                isStyled
                pubStates={[PUB_STATES.USED, PUB_STATES.NEW_USED]}
                useFloatingLabel
                id="year"
              />
            </Col>
            <Col xs={12} md={5} lg className="mb-1 mb-md-0">
              <MakeDropdown
                year={year}
                name="select-make"
                toggle="Make"
                valueKey="niceName"
                labelKey="name"
                labelText="Make"
                onChange={this.setMake}
                value={make}
                isStyled
                useFloatingLabel
                pubStates={[PUB_STATES.USED, PUB_STATES.NEW_USED]}
                id="make"
              />
            </Col>
            <Col xs={12} md={5} lg>
              <ModelDropdown
                make={make}
                year={year}
                name="select-model"
                toggle="Model"
                valueKey="niceId"
                labelKey="name"
                labelText="Model"
                value={model}
                onChange={this.setModel}
                isStyled
                useFloatingLabel
                pubStates={[PUB_STATES.USED, PUB_STATES.NEW_USED]}
                id="model"
              />
            </Col>
            <Col xs={12} md={12} lg className="mt-1 mt-lg-0">
              <Button
                onClick={this.trackSubmit}
                className="cta-btn d-flex justify-content-center text-transform-none size-16 text-white"
                tag={Link}
                target="_blank"
                rel="noopener noreferrer"
                to={appraisalHistoryUrl}
                color={btnColor}
                data-tracking-id="appraise_your_car"
                data-tracking-value={CTA_TEXT}
                disabled={isBtnDisabled}
                role="link"
                aria-disabled={isBtnDisabled}
              >
                {CTA_TEXT}
              </Button>
            </Col>
          </Row>
        </div>
      </fieldset>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
});

export const TrackCarValueModule = connect(mapStateToProps)(TrackCarValueModuleUI);
